import { useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { connectorsByIdentifier } from "../../shared/connectors";
import { getInitialConnectorValues } from "../../shared/connectors/getInitialConnectorValues";
import { sharedMutators } from "../../shared/mutators";
import { useConnectorSelectOnSave } from "./useConnectorSelectOnSave";
import { useDefaultConnectorGrants } from "./useDefaultConnectorGrants";
import { navigate, routes } from "../lib/urls";

// Creates a default connector of the given type, opens it in the connector
// modal to configure, and selects it via onCreated once saved. The create is
// optimistic, so the modal opens immediately rather than after a round-trip.
export function useConnectorCreator(
  onCreated: (connectedAppId: string) => void,
  spaceId?: string
) {
  const z = useZero();
  const defaultConnectorGrants = useDefaultConnectorGrants(spaceId);
  const track = useConnectorSelectOnSave((connector) =>
    onCreated(connector.id)
  );

  return function createConnector(
    identifier: string,
    overrides?: {
      name?: string;
      configuration?: Record<string, string>;
      urlPattern?: string;
    }
  ) {
    const definition = connectorsByIdentifier[identifier];
    if (!definition || !defaultConnectorGrants) return;
    const initial = getInitialConnectorValues(definition);
    const id = nanoid(12);
    z.mutate(
      sharedMutators.createConnectedApp({
        connectedAppId: id,
        name: overrides?.name ?? (initial.name || definition.name),
        identifier,
        urlPattern: overrides?.urlPattern ?? initial.urlPattern,
        configuration: {
          ...initial.configuration,
          ...overrides?.configuration,
        },
        secrets: {},
        createOAuthConnection: initial.createOAuthConnection,
        grants: defaultConnectorGrants,
      })
    );
    track(id);
    navigate(routes.modal.connector, { id: id });
  };
}
